
<script>
    import { onMount, onDestroy } from 'svelte';
    import { slide } from 'svelte/transition';
    import { Chart, registerables } from 'chart.js';
    import AgGridSvelte from 'ag-grid-svelte';
    import 'ag-grid-community/styles/ag-grid.css';
    import 'ag-grid-community/styles/ag-theme-alpine.css';
    import * as config from './config.js';
    import Navigation from './components/layout/Navigation.svelte';

    export let setPage;
    export let user;
    export let onLogout = null; // App.svelte의 handleLogout 함수

    Chart.register(...registerables);

    // 공통 API 응답 처리 함수
    function handleApiResponse(response) {
        if (response.status === 401 || response.status === 403) {
            console.warn('인증 실패. 로그인 페이지로 이동합니다.');
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            setPage('login');
            return false;
        }
        return true;
    }

    // === 모바일 반응형 지원 추가 ===
    let isMobile = false;

    function checkMobileDevice() {
        if (typeof window === 'undefined') return false;
        const ua = window.navigator.userAgent.toLowerCase();
        const keywords = ['android', 'iphone', 'ipad', 'ipod', 'windows phone', 'mobile'];
        return keywords.some(k => ua.includes(k)) || window.innerWidth < 768;
    }

    function handleResize() {
        const prev = isMobile;
        isMobile = checkMobileDevice();
        if (prev !== isMobile) {
            // 모바일/데스크톱 전환 시 그리드 업데이트
            if (gridApi) {
                gridApi.setDomLayout(isMobile ? 'autoHeight' : 'normal');
                gridApi.resetRowHeights();
                gridApi.setRowData(dailyStats);
            }
            if (appliedGridApi) {
                appliedGridApi.setDomLayout(isMobile ? 'autoHeight' : 'normal');
                appliedGridApi.resetRowHeights();
            }
            if (reviewGridApi) {
                reviewGridApi.setDomLayout(isMobile ? 'autoHeight' : 'normal');
                reviewGridApi.resetRowHeights();
            }
            if (progressGridApi) {
                progressGridApi.setDomLayout(isMobile ? 'autoHeight' : 'normal');
                progressGridApi.resetRowHeights();
            }
        }
    }

    let dailyStats = [];
    let totalStats = {};
    let trendData = [];
    let applicantsByStatus = [];
    let isLoading = true;
    let errorMessage = '';

    let lineChart;
    let doughnutChart;

    // AG Grid - 일자별 상세 현황
    let gridApi;
    
    // 데스크톱용 컬럼 정의
    const desktopColumnDefs = [
        { headerName: '날짜', field: 'date', sortable: true, filter: true, width: 120, flex: 1 },
        { headerName: '전체', field: 'total', sortable: true, filter: true, width: 80, flex: 0.8 },
        { headerName: '신청됨', field: '신청됨', sortable: true, filter: true, width: 80, flex: 0.8 },
        { headerName: '검토', field: '검토', sortable: true, filter: true, width: 80, flex: 0.8 },
        { headerName: '진행중', field: '진행중', sortable: true, filter: true, width: 80, flex: 0.8 },
        { headerName: '합격', field: '합격', sortable: true, filter: true, width: 80, flex: 0.8 },
        { headerName: '불합격', field: '불합격', sortable: true, filter: true, width: 80, flex: 0.8 },
        { headerName: '프로젝트 취소', field: '프로젝트 취소', sortable: true, filter: true, width: 120, flex: 1 },
        { headerName: '프로젝트 연기', field: '프로젝트 연기', sortable: true, filter: true, width: 120, flex: 1 },
        { headerName: '마감', field: '마감', sortable: true, filter: true, width: 80, flex: 0.8 },
        { headerName: '취소', field: '취소', sortable: true, filter: true, width: 80, flex: 0.8 },
        { headerName: '취소', field: '취소', sortable: true, filter: true, width: 80, flex: 0.8 },
        { headerName: '본인확인중', field: '본인확인중', sortable: true, filter: true, width: 120, flex: 1 }
    ];
    
    // 모바일용 컬럼 정의 - 카드형 렌더링
    const mobileColumnDefs = [
        {
            headerName: '일자별 현황',
            field: 'date',
            minWidth: 300,
            flex: 1,
            cellRenderer: 'dailyStatCardRenderer',
            autoHeight: true,
            wrapText: true,
            sortable: true,
            editable: false
        }
    ];
    
    // 반응형 컬럼 정의
    $: columnDefs = isMobile ? mobileColumnDefs : desktopColumnDefs;
    $: gridOptions = {
        domLayout: isMobile ? 'autoHeight' : 'normal',
        components: { dailyStatCardRenderer },
        defaultColDef: {
            resizable: !isMobile,
            cellStyle: { textAlign: 'center', padding: isMobile ? '6px 4px' : '8px' },
            headerComponentParams: {
                style: { textAlign: 'center', fontWeight: 'bold' }
            }
        },
        suppressHorizontalScroll: isMobile,
        enableCellTextSelection: true,
        ensureDomOrder: true,
        rowHeight: isMobile ? undefined : 50
    };

    function onGridReady(params) {
        gridApi = params.api;
    }
    
    // 모바일용 일자별 현황 카드 렌더러
    function dailyStatCardRenderer(params) {
        const data = params.data;
        if (!data) return '';
        
        const date = data.date || '-';
        const total = data.total || 0;
        const stats = [
            { label: '신청됨', value: data['신청됨'] || 0, color: '#3273dc' },
            { label: '검토', value: data['검토'] || 0, color: '#ffdd57' },
            { label: '진행중', value: data['진행중'] || 0, color: '#48c774' },
            { label: '합격', value: data['합격'] || 0, color: '#00d1b2' },
            { label: '불합격', value: data['불합격'] || 0, color: '#f14668' },
            { label: '프로젝트 취소', value: data['프로젝트 취소'] || 0, color: '#8a4d76' },
            { label: '프로젝트 연기', value: data['프로젝트 연기'] || 0, color: '#b8b8b8' },
            { label: '마감', value: data['마감'] || 0, color: '#616161' },
            { label: '취소', value: data['취소'] || 0, color: '#363636' },
            { label: '본인확인중', value: data['본인확인중'] || 0, color: '#3273dc' }
        ];
        
        // 0이 아닌 값들만 필터링
        const nonZeroStats = stats.filter(stat => stat.value > 0);
        
        const statsHtml = nonZeroStats.map(stat => 
            `<div class="stat-item">
                <span class="stat-label">${stat.label}</span>
                <span class="stat-value" style="color: ${stat.color}">${stat.value}</span>
            </div>`
        ).join('');
        
        return `
            <div class="daily-stat-card">
                <div class="card-header">
                    <div class="date">${date}</div>
                    <div class="total-badge">총 ${total}건</div>
                </div>
                <div class="card-body">
                    <div class="stats-grid">
                        ${statsHtml}
                    </div>
                </div>
            </div>
        `;
    }

    // AG Grid - 상태별 테이블
    let appliedGridApi, reviewGridApi, progressGridApi;
    
    // 상태별 테이블 컬럼 정의
    const statusColumnDefs = [
        { headerName: '공고명', field: 'project_title', sortable: true, filter: true, flex: 2 },
        { headerName: '지원자', field: 'applicant_name', sortable: true, filter: true, flex: 1 },
        { headerName: '나이', field: 'age', sortable: true, filter: true, width: 80, cellRenderer: params => params.value || '-' },
        { headerName: '단가', field: 'daily_rate', sortable: true, filter: true, width: 100, cellRenderer: params => params.value || '-' },
        { headerName: '지원일자', field: 'application_date', sortable: true, filter: true, width: 120, cellRenderer: params => new Date(params.value).toLocaleDateString() }
    ];
    
    // 상태별 테이블 옵션
    $: statusGridOptions = {
        domLayout: isMobile ? 'autoHeight' : 'normal',
        defaultColDef: {
            resizable: !isMobile,
            cellStyle: { textAlign: 'center', padding: isMobile ? '6px 4px' : '8px' },
            headerComponentParams: {
                style: { textAlign: 'center', fontWeight: 'bold' }
            }
        },
        suppressHorizontalScroll: isMobile,
        enableCellTextSelection: true,
        ensureDomOrder: true,
        pagination: true,
        paginationPageSize: isMobile ? 5 : 10,
        rowHeight: isMobile ? undefined : 50
    };

    function onAppliedGridReady(params) {
        appliedGridApi = params.api;
    }

    function onReviewGridReady(params) {
        reviewGridApi = params.api;
    }

    function onProgressGridReady(params) {
        progressGridApi = params.api;
    }

    // 상태별 데이터 필터링
    $: appliedData = applicantsByStatus.filter(app => app.status === '신청됨');
    $: reviewData = applicantsByStatus.filter(app => app.status === '검토');
    $: progressData = applicantsByStatus.filter(app => app.status === '진행중');

    // 권한 확인
    let isAdmin = false;
    let isCompany = false;
    
    if (typeof window !== 'undefined') {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                user = JSON.parse(storedUser);
                isAdmin = config.isAdminUser(user);
                isCompany = user.role === 'company';
            } catch (e) {
                console.error('사용자 정보 파싱 오류:', e);
            }
        }
    }

    // 페이지 변경 이벤트 리스너
    function handlePageChange(event) {
        setPage(event.detail.page);
    }
    
    // 로그아웃 이벤트 리스너
    function handleLogout() {
        if (typeof window !== 'undefined') {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        }
        setPage('home');
    }
    
    // 로고 클릭 이벤트 리스너
    function handleLogoClick() {
        setPage('home');
    }

    // 상태별 테이블 접기/펼치기 상태
    let expandedSections = {
        신청됨: true,
        검토중: true,
        진행중: true
    };

    function toggleSection(sectionName) {
        expandedSections[sectionName] = !expandedSections[sectionName];
    }


    onMount(async () => {
        // 토큰 및 권한 체크
        const token = localStorage.getItem('token');
        if (!token) {
            console.warn('토큰이 없습니다. 로그인 페이지로 이동합니다.');
            setPage('login');
            return;
        }

        // 이벤트 리스너 등록
        if (typeof window !== 'undefined') {
            isMobile = checkMobileDevice();
            window.addEventListener('pagechange', handlePageChange);
            window.addEventListener('logout', handleLogout);
            window.addEventListener('logoclick', handleLogoClick);
            window.addEventListener('resize', handleResize);
        }
        
        await fetchData();
        if (!errorMessage) {
            createCharts();
        }
    });

    async function fetchData() {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/dashboard/stats`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                if (!handleApiResponse(response)) return;
                throw new Error('Failed to fetch dashboard data');
            }

            const data = await response.json();
            dailyStats = processDailyStats(data.daily);
            totalStats = processTotalStats(data.total);
            trendData = data.trend;
            await fetchApplicantsByStatus();
            isLoading = false;
        } catch (err) {
            errorMessage = err.message;
            isLoading = false;
        }
    }

    function processDailyStats(dailyData) {
        const statusOrder = ['신청됨', '검토', '진행중', '합격', '불합격', '프로젝트 취소', '프로젝트 연기', '마감', '취소', '본인확인중'];
        const statsByDate = {};

        dailyData.forEach(item => {
            const date = new Date(item.date).toLocaleDateString();
            if (!statsByDate[date]) {
                statsByDate[date] = { date, total: 0 };
                statusOrder.forEach(s => statsByDate[date][s] = 0);
            }
            // 한국어 상태명 사용
            const statusName = item.status_name || getStatusName(item.status);
            statsByDate[date][statusName] = Number(item.count);
            statsByDate[date].total += Number(item.count);
        });

        return Object.values(statsByDate);
    }

    function processTotalStats(totalData) {
        const stats = { total: 0 };
        totalData.forEach(item => {
            // 한국어 상태명 사용
            const statusName = item.status_name || getStatusName(item.status);
            stats[statusName] = Number(item.count);
            stats.total += Number(item.count);
        });
        return stats;
    }

    // 숫자 상태를 한국어로 변환하는 헬퍼 함수
    function getStatusName(status) {
        const statusMap = {
            0: '신청됨',
            1: '검토',
            2: '진행중',
            3: '합격',
            4: '불합격',
            5: '프로젝트 취소',
            6: '프로젝트 연기',
            7: '마감',
            8: '지원 취소',
            9: '본인확인중'
        };
        return statusMap[status] || '알 수 없음';
    }

    async function fetchApplicantsByStatus() {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/applications/by-status`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                if (!handleApiResponse(response)) return;
                throw new Error('Failed to fetch applicants by status');
            }

            const data = await response.json();
            applicantsByStatus = data;
        } catch (err) {
            console.error('지원자 상태별 데이터 로드 오류:', err);
        }
    }

    function createCharts() {
        const lineCtx = document.getElementById('trendChart').getContext('2d');
        lineChart = new Chart(lineCtx, {
            type: 'line',
            data: {
                labels: trendData.map(d => new Date(d.date).toLocaleDateString()),
                datasets: [{
                    label: '일일 지원자 수',
                    data: trendData.map(d => d.count),
                    borderColor: '#3273dc',
                    tension: 0.1
                }]
            }
        });

        const doughnutCtx = document.getElementById('statusChart').getContext('2d');
        doughnutChart = new Chart(doughnutCtx, {
            type: 'doughnut',
            data: {
                labels: Object.keys(totalStats).filter(k => k !== 'total'),
                datasets: [{
                    data: Object.values(totalStats).filter((v, i) => Object.keys(totalStats)[i] !== 'total'),
                    backgroundColor: ['#3273dc', '#ffdd57', '#48c774', '#f14668', '#8a4d76', '#b8b8b8', '#616161']
                }]
            }
        });
    }
    
    // 컴포넌트 정리 시 이벤트 리스너 제거
    onDestroy(() => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('pagechange', handlePageChange);
            window.removeEventListener('logout', handleLogout);
            window.removeEventListener('logoclick', handleLogoClick);
            window.removeEventListener('resize', handleResize);
        }
    });
</script>

<!-- 상단 네비게이션 -->
<Navigation 
    {user}
    currentPage="admin-dashboard"
    {onLogout}
/>
<div class="container">
    {#if isLoading}
        <p>데이터를 불러오는 중...</p>
    {:else if errorMessage}
        <p class="error-message">{errorMessage}</p>
    {:else}
        <!-- KPI Cards -->
        <div class="kpi-cards">
            <div class="card">
                <h3>총 지원자 수</h3>
                <p>{totalStats.total || 0}</p>
            </div>
            <div class="card">
                <h3>검토 중</h3>
                <p>{totalStats['검토'] || 0}</p>
            </div>
            <div class="card">
                <h3>진행 중</h3>
                <p>{totalStats['진행중'] || 0}</p>
            </div>
            <div class="card">
                <h3>합격</h3>
                <p>{totalStats['합격'] || 0}</p>
            </div>
        </div>

        <!-- Status Tables -->
        <div class="status-tables">
            <div class="status-table-section">
                <button class="status-header clickable" on:click={() => toggleSection('신청됨')}>
                    <span class="header-text">신청됨</span>
                    <span class="toggle-icon" class:expanded={expandedSections.신청됨}>▼</span>
                </button>
                {#if expandedSections.신청됨}
                    <div class="status-table" transition:slide={{duration: 300}}>
                        {#if appliedData.length === 0}
                            <div class="empty-table">신청된 지원자가 없습니다.</div>
                        {:else}
                            <div class="ag-theme-alpine status-grid" class:mobile-grid={isMobile} style="width: 100%; height: {isMobile ? 'auto' : '400px'};">
                                <AgGridSvelte
                                    rowData={appliedData}
                                    columnDefs={statusColumnDefs}
                                    gridOptions={statusGridOptions}
                                    onGridReady={onAppliedGridReady}
                                />
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>

            <div class="status-table-section">
                <button class="status-header clickable" on:click={() => toggleSection('검토중')}>
                    <span class="header-text">검토중</span>
                    <span class="toggle-icon" class:expanded={expandedSections.검토중}>▼</span>
                </button>
                {#if expandedSections.검토중}
                    <div class="status-table" transition:slide={{duration: 300}}>
                        {#if reviewData.length === 0}
                            <div class="empty-table">검토 중인 지원자가 없습니다.</div>
                        {:else}
                            <div class="ag-theme-alpine status-grid" class:mobile-grid={isMobile} style="width: 100%; height: {isMobile ? 'auto' : '400px'};">
                                <AgGridSvelte
                                    rowData={reviewData}
                                    columnDefs={statusColumnDefs}
                                    gridOptions={statusGridOptions}
                                    onGridReady={onReviewGridReady}
                                />
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>

            <div class="status-table-section">
                <button class="status-header clickable" on:click={() => toggleSection('진행중')}>
                    <span class="header-text">진행중</span>
                    <span class="toggle-icon" class:expanded={expandedSections.진행중}>▼</span>
                </button>
                {#if expandedSections.진행중}
                    <div class="status-table" transition:slide={{duration: 300}}>
                        {#if progressData.length === 0}
                            <div class="empty-table">진행 중인 지원자가 없습니다.</div>
                        {:else}
                            <div class="ag-theme-alpine status-grid" class:mobile-grid={isMobile} style="width: 100%; height: {isMobile ? 'auto' : '400px'};">
                                <AgGridSvelte
                                    rowData={progressData}
                                    columnDefs={statusColumnDefs}
                                    gridOptions={statusGridOptions}
                                    onGridReady={onProgressGridReady}
                                />
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        </div>

        <!-- Charts -->
        <div class="charts-grid">
            <div class="chart-container">
                <h3>최근 7일 지원 트렌드</h3>
                <canvas id="trendChart"></canvas>
            </div>
            <div class="chart-container">
                <h3>지원 상태 분포</h3>
                <canvas id="statusChart"></canvas>
            </div>
        </div>

        <!-- Data Table -->
        <div class="table-container">
            <h3>일자별 상세 현황</h3>
            <div class="ag-theme-alpine compact-grid" class:mobile-grid={isMobile} style="width: 100%; height: {isMobile ? 'auto' : '600px'};">
                <AgGridSvelte
                    rowData={dailyStats}
                    columnDefs={columnDefs}
                    gridOptions={gridOptions}
                    pagination={true}
                    paginationPageSize={isMobile ? 5 : 20}
                    onGridReady={onGridReady}
                />
            </div>
        </div>
    {/if}
</div>

<style>
    .container {
        padding: 2rem;
    }
    .kpi-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 1rem;
        margin-bottom: 2rem;
    }
    .card {
        background: #fff;
        padding: 1.5rem;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        text-align: center;
    }
    .card h3 {
        margin: 0 0 0.5rem 0;
        font-size: 1rem;
        color: #555;
    }
    .card p {
        margin: 0;
        font-size: 2rem;
        font-weight: bold;
        color: #3273dc;
    }
    .charts-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 2rem;
        margin-bottom: 2rem;
    }
    .chart-container {
        background: #fff;
        padding: 1.5rem;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .table-container {
        background: #fff;
        padding: 1.5rem;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        overflow-x: auto;
    }
    .error-message {
        color: red;
    }
    
    /* Status Tables Styles */
    .status-tables {
        margin-bottom: 2rem;
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }
    
    .status-table-section {
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        overflow: hidden;
    }
    
    .status-header {
        background: #f8f9fa;
        margin: 0;
        padding: 1rem 1.5rem;
        font-size: 1.2rem;
        font-weight: bold;
        border-bottom: 2px solid #e9ecef;
        border: none;
        width: 100%;
        text-align: left;
        color: #333;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    
    .status-header.clickable {
        cursor: pointer;
        transition: background-color 0.2s ease;
    }
    
    .status-header.clickable:hover {
        background: #e9ecef;
    }
    
    .header-text {
        flex: 1;
    }
    
    .toggle-icon {
        font-size: 0.8rem;
        transition: transform 0.3s ease;
        color: #666;
    }
    
    .toggle-icon.expanded {
        transform: rotate(180deg);
    }
    
    .status-table {
        overflow-x: auto;
    }
    
    
    .empty-table {
        padding: 2rem;
        text-align: center;
        color: #6c757d;
        font-style: italic;
        background: #f8f9fa;
    }
    
    /* AG Grid 커스텀 스타일 */
    :global(.compact-grid .ag-header-cell) {
        padding: 8px 4px !important;
        font-size: 0.9rem !important;
        font-weight: 600 !important;
    }
    
    :global(.compact-grid .ag-cell) {
        padding: 6px 4px !important;
        font-size: 0.85rem !important;
        line-height: 1.2 !important;
    }
    
    :global(.compact-grid .ag-row) {
        height: 32px !important;
    }
    
    :global(.compact-grid .ag-header-row) {
        height: 40px !important;
    }
    
    :global(.compact-grid) {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
    }
    
    :global(.compact-grid .ag-paging-panel) {
        padding: 8px 16px !important;
        font-size: 0.85rem !important;
    }
    
    /* 상태별 테이블 AG Grid 스타일 */
    :global(.status-grid .ag-header-cell) {
        padding: 8px 6px !important;
        font-size: 0.9rem !important;
        font-weight: 600 !important;
        background-color: #f8f9fa !important;
    }
    
    :global(.status-grid .ag-cell) {
        padding: 8px 6px !important;
        font-size: 0.85rem !important;
        line-height: 1.3 !important;
    }
    
    :global(.status-grid .ag-row) {
        height: 40px !important;
    }
    
    :global(.status-grid .ag-header-row) {
        height: 44px !important;
    }
    
    :global(.status-grid) {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
        border-radius: 0 !important;
    }
    
    :global(.status-grid .ag-paging-panel) {
        padding: 8px 16px !important;
        font-size: 0.85rem !important;
        background-color: #f8f9fa !important;
    }
    
    :global(.status-grid .ag-root-wrapper) {
        border-radius: 0 !important;
    }
    
    :global(.status-grid .ag-header) {
        border-bottom: 1px solid #dee2e6 !important;
    }
    
    :global(.status-grid .ag-row-hover) {
        background-color: #f8f9fa !important;
    }
    
    /* 모바일 반응형 스타일 */
    @media (max-width: 768px) {
        .container {
            padding: 1rem;
        }
        
        .kpi-cards {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.75rem;
            margin-bottom: 1.5rem;
        }
        
        .card {
            padding: 1rem;
        }
        
        .card h3 {
            font-size: 0.9rem;
        }
        
        .card p {
            font-size: 1.5rem;
        }
        
        .charts-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
            margin-bottom: 1.5rem;
        }
        
        .chart-container {
            padding: 1rem;
        }
        
        .status-tables {
            gap: 1rem;
        }
        
        .status-header {
            padding: 0.75rem 1rem;
            font-size: 1rem;
        }
        
        .table-container {
            padding: 1rem;
        }
        
        .table-container h3 {
            font-size: 1.1rem;
            margin-bottom: 1rem;
        }
    }
    
    /* 모바일 그리드 전용 스타일 */
    :global(.mobile-grid) {
        height: auto !important;
    }
    
    :global(.mobile-grid .ag-header-cell) {
        padding: 4px 2px !important;
        font-size: 0.8rem !important;
        font-weight: 600 !important;
    }
    
    :global(.mobile-grid .ag-cell) {
        padding: 4px 2px !important;
        font-size: 0.75rem !important;
        line-height: 1.2 !important;
    }
    
    :global(.mobile-grid .ag-row) {
        height: 32px !important;
    }
    
    :global(.mobile-grid .ag-header-row) {
        height: 36px !important;
    }
    
    :global(.mobile-grid .ag-paging-panel) {
        padding: 6px 12px !important;
        font-size: 0.8rem !important;
    }
    
    :global(.mobile-grid .ag-paging-page-size) {
        display: none !important;
    }
    
    :global(.mobile-grid .ag-paging-description) {
        display: none !important;
    }
    
    /* 모바일에서 수평 스크롤 지원 */
    :global(.mobile-grid .ag-body-horizontal-scroll) {
        overflow-x: auto !important;
    }
    
    :global(.mobile-grid .ag-body-viewport) {
        overflow-x: auto !important;
    }
    
    :global(.mobile-grid .ag-center-cols-container) {
        min-width: 100% !important;
    }
    
    /* 일자별 현황 카드 스타일 */
    :global(.daily-stat-card) {
        padding: 12px;
        border-radius: 8px;
        background: #ffffff;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        margin: 3px 0;
        border: 1px solid #e9ecef;
    }
    
    :global(.daily-stat-card .card-header) {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        padding-bottom: 8px;
        border-bottom: 1px solid #e9ecef;
    }
    
    :global(.daily-stat-card .date) {
        font-size: 16px;
        font-weight: 600;
        color: #333;
    }
    
    :global(.daily-stat-card .total-badge) {
        background: #3273dc;
        color: white;
        padding: 4px 10px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
    }
    
    :global(.daily-stat-card .card-body) {
        margin-top: 8px;
    }
    
    :global(.daily-stat-card .stats-grid) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 6px;
    }
    
    :global(.daily-stat-card .stat-item) {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px 8px;
        background: #f8f9fa;
        border-radius: 4px;
        font-size: 12px;
    }
    
    :global(.daily-stat-card .stat-label) {
        color: #6c757d;
        font-weight: 500;
    }
    
    :global(.daily-stat-card .stat-value) {
        font-weight: 600;
        font-size: 14px;
    }
    
    /* 카드가 너무 작을 때를 위한 추가 스타일 */
    @media (max-width: 480px) {
        :global(.daily-stat-card .stats-grid) {
            grid-template-columns: 1fr;
        }
        
        :global(.daily-stat-card .stat-item) {
            padding: 6px 10px;
        }
    }
    
    /* 모바일 그리드 헤더 숨기기 */
    :global(.mobile-grid .ag-header) {
        display: none;
    }
</style>
